/**
 * Project Report — comparative view of all projects
 * Shows budget usage, spending, and status across all projects
 */
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { Project } from '../../types';

interface ProjectReportProps {
  projects: Project[];
}

type SortField = 'name' | 'budget' | 'spent' | 'available' | 'percentage';
type SortOrder = 'asc' | 'desc';

const ProjectReport: React.FC<ProjectReportProps> = ({ projects }) => {
  const navigate = useNavigate();
  const [sortField, setSortField] = useState<SortField>('spent');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const formatCurrency = (amount: number): string =>
    new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
    }).format(amount);

  const filtered = useMemo(() => {
    let list = [...projects];
    if (statusFilter !== 'all') {
      list = list.filter((p) => p.status === statusFilter);
    }
    return list;
  }, [projects, statusFilter]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let valA: number | string;
      let valB: number | string;
      switch (sortField) {
        case 'name':
          valA = a.name.toLowerCase();
          valB = b.name.toLowerCase();
          return sortOrder === 'asc'
            ? (valA as string).localeCompare(valB as string)
            : (valB as string).localeCompare(valA as string);
        case 'budget':
          valA = a.budget; valB = b.budget; break;
        case 'spent':
          valA = a.spent; valB = b.spent; break;
        case 'available':
          valA = a.available; valB = b.available; break;
        case 'percentage':
          valA = a.budget > 0 ? a.spent / a.budget : 0;
          valB = b.budget > 0 ? b.spent / b.budget : 0;
          break;
        default:
          valA = 0; valB = 0;
      }
      return sortOrder === 'asc'
        ? (valA as number) - (valB as number)
        : (valB as number) - (valA as number);
    });
  }, [filtered, sortField, sortOrder]);

  // Totals
  const totalBudget = filtered.reduce((s, p) => s + p.budget, 0);
  const totalSpent = filtered.reduce((s, p) => s + p.spent, 0);
  const totalAvailable = filtered.reduce((s, p) => s + p.available, 0);
  const overBudgetCount = filtered.filter((p) => p.budgetStatus === 'over').length;

  // Chart data — top 10 by spent
  const chartData = [...filtered]
    .sort((a, b) => b.spent - a.spent)
    .slice(0, 10)
    .map((p) => ({
      name: p.name.length > 20 ? p.name.slice(0, 18) + '…' : p.name,
      presupuesto: p.budget,
      gastado: p.spent,
      projectId: p.projectId,
    }));

  const getBarColor = (pct: number) => {
    if (pct >= 100) return '#ef4444';
    if (pct >= 80) return '#f59e0b';
    return '#22c55e';
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <span className="text-gray-300 ml-1">↕</span>;
    return <span className="text-blue-600 ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>;
  };

  const statusCounts = {
    all: projects.length,
    active: projects.filter((p) => p.status === 'active').length,
    completed: projects.filter((p) => p.status === 'completed').length,
    suspended: projects.filter((p) => p.status === 'suspended').length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Reporte por Proyecto</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-600">Proyectos:</span>
            <span className="ml-2 font-semibold">{filtered.length}</span>
          </div>
          <div>
            <span className="text-gray-600">Presupuesto Total:</span>
            <span className="ml-2 font-semibold">{formatCurrency(totalBudget)}</span>
          </div>
          <div>
            <span className="text-gray-600">Total Gastado:</span>
            <span className="ml-2 font-semibold text-blue-600">{formatCurrency(totalSpent)}</span>
          </div>
          <div>
            <span className="text-gray-600">Excedidos:</span>
            <span className={`ml-2 font-semibold ${overBudgetCount > 0 ? 'text-red-600' : 'text-green-600'}`}>
              {overBudgetCount}
            </span>
          </div>
        </div>
      </div>

      {/* Status Filter */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex flex-wrap gap-2">
          {(['all', 'active', 'completed', 'suspended'] as const).map((s) => {
            const labels: Record<string, string> = {
              all: 'Todos',
              active: 'Activos',
              completed: 'Finalizados',
              suspended: 'Detenidos',
            };
            const colors: Record<string, string> = {
              all: 'bg-gray-600',
              active: 'bg-green-600',
              completed: 'bg-blue-600',
              suspended: 'bg-yellow-600',
            };
            return (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  statusFilter === s
                    ? `${colors[s]} text-white`
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {labels[s]} ({statusCounts[s]})
              </button>
            );
          })}
        </div>
      </div>

      {/* Chart */}
      {chartData.length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-4">Presupuesto vs Gastado (Top 10)</h3>
          <div className="h-72 lg:h-96">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 5, right: 20, left: 20, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-35} textAnchor="end" height={80} className="text-xs" />
                <YAxis className="text-xs" />
                <Tooltip formatter={(value: number) => formatCurrency(value)} />
                <Bar dataKey="presupuesto" fill="#93c5fd" name="Presupuesto" />
                <Bar dataKey="gastado" fill="#3b82f6" name="Gastado" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {/* Desktop */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-gray-700"
                  onClick={() => handleSort('name')}
                >
                  Proyecto <SortIcon field="name" />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Estado
                </th>
                <th
                  className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-gray-700"
                  onClick={() => handleSort('budget')}
                >
                  Presupuesto <SortIcon field="budget" />
                </th>
                <th
                  className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-gray-700"
                  onClick={() => handleSort('spent')}
                >
                  Gastado <SortIcon field="spent" />
                </th>
                <th
                  className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-gray-700"
                  onClick={() => handleSort('available')}
                >
                  Disponible <SortIcon field="available" />
                </th>
                <th
                  className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-gray-700"
                  onClick={() => handleSort('percentage')}
                >
                  Uso <SortIcon field="percentage" />
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {sorted.map((project) => {
                const pct = project.budget > 0 ? Math.round((project.spent / project.budget) * 100) : 0;
                return (
                  <tr
                    key={project.projectId}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/projects/${project.projectId}`)}
                  >
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{project.name}</div>
                      {project.description && (
                        <div className="text-xs text-gray-500 truncate max-w-xs">{project.description}</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        project.status === 'active' ? 'bg-green-100 text-green-800' :
                        project.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {project.status === 'active' ? 'Activo' :
                         project.status === 'completed' ? 'Completado' : 'Suspendido'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-gray-900">
                      {formatCurrency(project.budget)}
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-semibold text-gray-900">
                      {formatCurrency(project.spent)}
                    </td>
                    <td className="px-6 py-4 text-right text-sm">
                      <span className={project.available < 0 ? 'text-red-600 font-semibold' : 'text-green-600'}>
                        {formatCurrency(project.available)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-20 bg-gray-200 rounded-full h-2">
                          <div
                            className="h-2 rounded-full transition-all"
                            style={{
                              width: `${Math.min(pct, 100)}%`,
                              backgroundColor: getBarColor(pct),
                            }}
                          />
                        </div>
                        <span className="text-xs text-gray-600 w-10 text-right">{pct}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-gray-100">
              <tr>
                <td className="px-6 py-4 font-bold text-gray-900" colSpan={2}>Total</td>
                <td className="px-6 py-4 text-right font-bold text-gray-900">{formatCurrency(totalBudget)}</td>
                <td className="px-6 py-4 text-right font-bold text-blue-600">{formatCurrency(totalSpent)}</td>
                <td className="px-6 py-4 text-right font-bold">
                  <span className={totalAvailable < 0 ? 'text-red-600' : 'text-green-600'}>
                    {formatCurrency(totalAvailable)}
                  </span>
                </td>
                <td className="px-6 py-4 text-right font-bold text-gray-900">
                  {totalBudget > 0 ? Math.round((totalSpent / totalBudget) * 100) : 0}%
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Mobile */}
        <div className="md:hidden p-4 space-y-4">
          {sorted.map((project) => {
            const pct = project.budget > 0 ? Math.round((project.spent / project.budget) * 100) : 0;
            return (
              <div
                key={project.projectId}
                className="border rounded-lg p-4 cursor-pointer hover:bg-gray-50"
                onClick={() => navigate(`/projects/${project.projectId}`)}
              >
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className="font-semibold text-gray-900">{project.name}</div>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mt-1 ${
                      project.status === 'active' ? 'bg-green-100 text-green-800' :
                      project.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {project.status === 'active' ? 'Activo' :
                       project.status === 'completed' ? 'Completado' : 'Suspendido'}
                    </span>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-blue-600">{formatCurrency(project.spent)}</div>
                    <div className="text-xs text-gray-500">de {formatCurrency(project.budget)}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div
                      className="h-2 rounded-full transition-all"
                      style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: getBarColor(pct) }}
                    />
                  </div>
                  <span className="text-xs text-gray-600">{pct}%</span>
                </div>
              </div>
            );
          })}
          <div className="border-t-2 border-gray-300 pt-4 flex justify-between items-center font-bold">
            <span className="text-gray-900">Total</span>
            <div className="text-right">
              <div className="text-lg text-blue-600">{formatCurrency(totalSpent)}</div>
              <div className="text-sm text-gray-600">de {formatCurrency(totalBudget)}</div>
            </div>
          </div>
        </div>

        {sorted.length === 0 && (
          <div className="p-12 text-center text-sm text-gray-500">
            No hay proyectos con el filtro seleccionado
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectReport;
